import React from "react";
import LazyLoad from "react-lazyload";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import { ChannelCardV2 } from "lib/helper/channel-interactive-tool-v2";
import { IChnlInteractiveThemeUI } from "lib/helper/channel-interactive-tool-theme";
import { iconBannerStyles } from "../../styles/component-styles";
import { getChannelIconOuterBoxStyles, getChannelIconInnerBoxStyles, channelIconMediaStyles, channelIconTooltipPopperProps, channelIconTooltipStyles, channelIconTileSize } from "./styles";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import ChannelInfoIcon from "@/shared/ChannelInfoIcon";
import { channelLogoSx, channelLogoChipFor, handleChannelLogoError } from "lib/helper/channel-logo-url";

interface ChannelIconProps {
  channel: ChannelCardV2;
  category: KeyValuePair;
  index: number;
  theme?: IChnlInteractiveThemeUI;
  isClickable: boolean;
  genreEnabled: boolean;
  onClick: (channel: ChannelCardV2) => void;
}

const ChannelIcon: React.FC<ChannelIconProps> = ({
  channel,
  category,
  index,
  theme,
  isClickable,
  genreEnabled,
  onClick
}) => {
  const handleMouseEnter = (e: React.MouseEvent<HTMLImageElement>) => {
    e.currentTarget.style.transform = isClickable ? "scale(1.1)" : "scale(1)";
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLImageElement>) => {
    e.currentTarget.style.transform = "scale(1)";
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (channel?.label) {
      // WAT-11362: was EmptySVG.svg — a blank image, so a 404'd OV logo (verified on
      // News Mix / Heroes & Icons) rendered an empty chip. Use the no-logo asset instead.
      handleChannelLogoError(e, channel?.isOvLogo, "emptySvg");
    } else if (e.currentTarget.parentElement) {
      e.currentTarget.parentElement.style.display = "none";
    }
  };

  const handleClick = () => {
    if (isClickable) {
      onClick(channel);
    }
  };

  if (!channel?.label) {
    return null;
  }

  const logoImg = (
    <CardMedia
      alt={channel.label || "channel logo icon"}
      aria-labelledby={`icon-container-${category.key}-${index}`}
      component={"img"}
      height="100px"
      src={channel.logo || "https://www.directv.com/dtvassets/rebrand/global/logos/dtv-logo.svg"}
      width="100px"
      {...(isClickable ? getAnalyticsAttrs(channel.label?.toLowerCase(), 'panel_channel lineup', `/modal_channel_${channel.label}`) : {})}
      sx={{
        ...channelIconMediaStyles,
        // WAT-11362: channelLogoSx cancels channelIconMediaStyles' borderRadius:50%
        // when the flag is on — a radius on the img CLIPS the artwork (proven on NFL
        // Network: "NETWORK" -> "VETWORK"). The circle is drawn by the container.
        // Flag OFF the original 50% radius stays, exactly as production renders it.
        ...channelLogoSx(channel?.isOvLogo, channel?.logo),
        position: 'relative',
        zIndex: 1
      }}
      onClick={handleClick}
      onError={handleError}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  );

  // WAT-11563: the lazyload placeholder must reserve the SAME box the outer style lays the
  // tile out in, so both come from this one helper rather than repeated literals.
  const tile = channelIconTileSize(genreEnabled);

  return (
    <DtvBox component="div" key={`${channel.id}-${index}`} sx={{
 ...getChannelIconOuterBoxStyles(theme, genreEnabled, isClickable),
position: "relative"
}}>
      <Tooltip
        describeChild
        PopperProps={channelIconTooltipPopperProps}
        closeOnScroll={true}
        content={channel.label}
        enterTouchDelay={0}
        key={index}
        placement={channel.isLocalChannel ? "top" : "bottom"}
        sx={channelIconTooltipStyles}
        tooltipStyles={iconBannerStyles.tooltipStyles}
      >
        {/* overflow={true} → react-lazyload auto-detects the nearest scrollable ancestor
            (#channels-container, overflow-y:scroll) and attaches its scroll listener there.
            height renders a placeholder div when the icon is not yet visible — critical fix:
            without a placeholder height every unloaded icon collapses to 0px, all 300+ appear
            at position 0, and react-lazyload loads them all at once. offset={100} pre-fetches
            100px before the icon scrolls into view.
            WAT-11563: the reservation MUST equal one rendered tile, or the collapsed grid's
            row boundary moves the moment logos load and the row at the cap is sliced through
            (it was a literal 112 against a 65px tile → 2.41 rows, the half-circles QA saw).
            🔴 It must also be RESPONSIVE. `height` renders one scalar div for every
            breakpoint, but the tile is 65px at xs and 78/96px at sm/md — a single xs number
            under-reserves ~30-40% per row there, so rows GROW on load (cumulative reflow
            under flexWrap) and ~1.4x more icons fall inside offset={100} at once: the exact
            load burst this placeholder exists to prevent. `placeholder` overrides `height`
            and can carry breakpoints, so it is the correct prop here. Every value comes from
            `channelIconTileSize`, the same helper the outer box lays the tile out with, so
            the reservation cannot drift from the rendered size — a hand-copied `96` here
            under-reserved by 18px per row whenever genreEnabled makes the sm tile 78px. */}
        {channel?.logo && (
          <LazyLoad
            once
            offset={100}
            overflow={true}
            placeholder={
              <DtvBox
                sx={{
                  height: {
                    xs: `${tile?.xs}px`,
                    sm: `${tile?.sm}px`,
                    md: `${tile?.md}px`
                  }
                }}
              />
            }
          >
            <DtvBox
              aria-label={`${channel.label} icon`}
              component="a"
              id={`icon-container-${category.key}-${index}`}
              role="button"
              sx={getChannelIconInnerBoxStyles(isClickable)}
              tabIndex={0}
            >
              {/* WAT-11362: with the flag ON every logo carries its own white circular
                  badge, so this grey disc would show through transparent artwork as a
                  second ring. Flag OFF it stays — it is the production loading state. */}
              {!channel?.isOvLogo && (
                <DtvBox
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '72%',
                    height: '72%',
                    backgroundColor: '#747474',
                    borderRadius: '50%',
                    zIndex: 0
                  }}
                />
              )}
              {/* WAT-11362: the chip CANNOT go on the <a> above — that box is inline, so
                  it drops width/height/transform and paints its background over the empty
                  inline fragments the block-level <img> leaves behind (white in computed
                  styles, invisible on screen). A block wrapper is the box that paints.
                  Rendered only when the flag is ON so the flag-OFF DOM is unchanged. */}
              {channel?.isOvLogo ? (
                <DtvBox
                  sx={{
                    display: "flex",
                    width: "100%",
                    // The wrapper has no intrinsic height, so the image's height:100% would
                    // collapse; a square ratio gives the circle its height before load too.
                    aspectRatio: "1",
                    ...channelLogoChipFor(channel?.isOvLogo, channel?.logo)
                  }}
                >
                  {logoImg}
                </DtvBox>
              ) : logoImg}
            </DtvBox>
          </LazyLoad>
        )}
      </Tooltip>
      {/* Generated by Copilot- Start - 2026-03-18 20:52:58 */}
      {channel.isLocalChannel && (() => {
        const isRsn = channel.rawData?.category === "RSN";
        const badgeLabel = isRsn ? "RSN" : "Local";
        const tooltipContent = isRsn ?
          "Regional Sports Networks aren't available everywhere and may vary by package and location. You may need to be in your billing region (ZIP Code) to watch them." :
          "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them.";

        return (
          <DtvBox
            component="span"
            sx={{
              textAlign: "center",
              width: "auto",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
              bottom: "0%"
            }}
          >
            <Tooltip
              describeChild
              closeOnScroll={true}
              content={tooltipContent}
              enterTouchDelay={0}
              leaveTouchDelay={3000}
              placement="bottom"
              PopperProps={{
                sx: {
                  "& .MuiTypography-root": {
                    fontFamily: "roboto-regular",
                    fontSize: "14px"
                  }
                }
              }}
              tooltipStyles={{
                backgroundColor: `#ffffff !important`,
                color: `#111111 !important`
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  cursor: "pointer",
                  color: "#ffff",
                  backgroundColor: "rgb(85, 85, 85)",
                  borderRadius: "4px",
                  letterSpacing: "-0.02em",
                  padding: "2px",
                  margin: "0px auto",
                  fontSize: "12px !important",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px"
                }}
              >
                {badgeLabel}
                <ChannelInfoIcon />
              </Typography>
            </Tooltip>
          </DtvBox>
        );
      })()}
      {/* Generated by Copilot- End - 2026-03-18 20:52:58 */}
    </DtvBox>
  );
};

// Memoize to prevent unnecessary re-renders when props haven't changed
// This is critical since ChannelIcon is rendered 100+ times
export default React.memo(ChannelIcon);
